import React, { useRef, useState, useEffect } from 'react';

const SearchResultsPanel = ({ 
  results, 
  chainDetection,
  circleAnalysis,
  isSaving, 
  onSave, 
  onRemovePlace,
  onShowChainModal,
  onShowAnalysisModal,
  searchResultsRef
}) => {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (searchResultsRef.current) {
        const rect = searchResultsRef.current.getBoundingClientRect();
        // Show button if results section is below viewport
        setShowScrollButton(rect.top > window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [searchResultsRef, results]);

  const scrollToResults = () => {
    searchResultsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (results.length === 0) return null;

  return (
    <>
      {/* Floating Scroll to Results Button */}
      {showScrollButton && (
        <button
          onClick={scrollToResults}
          className="fixed bottom-6 right-6 px-4 py-3 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600 transition-all z-50 flex items-center gap-2 animate-bounce"
        >
          <span className="text-sm font-medium">Search Results</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      <div ref={searchResultsRef} className="w-full bg-white border-t border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-screen mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span className="text-blue-500">💡</span>
              <span>Review and remove unwanted places, then click "Save Places"</span>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Circle Analysis Button */}
              {circleAnalysis && circleAnalysis.circles_at_limit > 0 && (
                <button
                  onClick={onShowAnalysisModal}
                  className="px-3 py-2 bg-orange-50 border border-orange-200 text-orange-700 rounded-md hover:bg-orange-100 text-xs font-medium flex items-center gap-1.5"
                >
                  ⚠ {circleAnalysis.circles_at_limit} circles at limit
                </button>
              )}

              {/* Chain Detection Button */}
              {chainDetection && chainDetection.unique_chain_count > 0 && (
                <button
                  onClick={onShowChainModal}
                  className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 text-xs font-medium flex items-center gap-1.5"
                >
                  🔗 {chainDetection.unique_chain_count} chains
                  <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded-full text-[10px]">
                    {chainDetection.total_chain_places}
                  </span>
                </button>
              )}

              {/* Save Places Button */}
              <button
                onClick={onSave}
                disabled={isSaving || results.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center text-sm font-medium"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    💾 Save {results.length} Places
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 max-w-screen mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {results.map((place) => (
              <div key={place.id} className="border border-gray-200 rounded-lg p-5 relative hover:shadow-md transition-shadow">
                <button
                  onClick={() => onRemovePlace([place.id], [])}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  title="Remove this place"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <h4 className="font-medium text-gray-900 mb-2 pr-6">{place.display_name}</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <p className="text-sm">📍 {place.formatted_address || 'N/A'}</p>
                  <p className="text-sm">📞 {place.international_phone_number || 'N/A'}</p>
                  <p className="text-sm">
                    🌐 {place.website_uri ? (
                      <a href={place.website_uri} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Visit Website</a>
                    ) : (
                      'N/A'
                    )}
                  </p>
                  <p className="text-sm">⭐ {place.rating ? `${place.rating} (${place.user_rating_count} reviews)` : 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResultsPanel;